import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Download, Printer, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Downloads = ({ isLightMode }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${BACKEND_URL}/api/brands`);
        setBrands(response.data);
    } catch (error) {
        console.error("Failed to fetch brands", error);
        toast.error("Could not load catalogue list");
    } finally {
        setLoading(false);
    }
  };

  const openPrintableForm = () => {
      // Open in new window for printing
      window.open('/trade/print-lead-form', '_blank');
  };

  return (
    <div className="space-y-8">
      
      {/* SECTION 1: PRINTABLE FORMS */}
      <Card className={`border ${isLightMode ? 'bg-white border-gray-200 text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
          <CardHeader>
              <CardTitle className="uppercase tracking-widest flex items-center gap-2">
                  <Printer size={20} className="text-dreibach-teal" />
                  Printable Forms
              </CardTitle>
              <CardDescription className={isLightMode ? 'text-gray-500' : 'text-gray-400'}>
                  Generate offline resources for trade shows and sales meetings.
              </CardDescription>
          </CardHeader>
          <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className={`p-6 border rounded-sm flex flex-col justify-between items-start gap-4 ${isLightMode ? 'bg-gray-50 border-gray-200' : 'bg-black/20 border-white/10'}`}>
                      <div>
                          <h4 className="font-bold uppercase text-sm mb-2">New Lead Capture Sheet</h4>
                          <p className={`text-xs ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
                              A4 blank template for manually capturing lead details at events. Clean layout with designated fields.
                          </p>
                      </div>
                      <Button 
                        onClick={openPrintableForm}
                        className="w-full bg-dreibach-teal hover:bg-white hover:text-black text-white uppercase font-bold text-xs tracking-widest"
                      >
                          <Printer size={14} className="mr-2" /> Open Print View
                      </Button>
                  </div>
              </div>
          </CardContent>
      </Card>

      {/* SECTION 2: BRAND CATALOGUES */}
      <Card className={`border ${isLightMode ? 'bg-white border-gray-200 text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
          <CardHeader>
              <CardTitle className="uppercase tracking-widest flex items-center gap-2">
                  <Download size={20} className="text-dreibach-teal" />
                  Brand Catalogues
              </CardTitle>
              <CardDescription className={isLightMode ? 'text-gray-500' : 'text-gray-400'}>
                  Access the latest PDF catalogues uploaded via the Brands Manager.
              </CardDescription>
          </CardHeader>
          <CardContent>
              {loading ? (
                  <div className="text-center py-8 text-gray-500 text-sm">Loading catalogues...</div>
              ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {brands.map((brand) => (
                          <div 
                            key={brand.id} 
                            className={`p-4 border rounded-sm flex items-center justify-between group ${isLightMode ? 'bg-gray-50 border-gray-200 hover:border-dreibach-teal' : 'bg-black/20 border-white/10 hover:border-dreibach-teal'}`}
                          >
                              <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-white p-1 flex items-center justify-center rounded-sm">
                                      {brand.logo ? (
                                          <img src={brand.logo} alt={brand.name} className="max-w-full max-h-full object-contain" />
                                      ) : (
                                          <FileText className="text-gray-400" />
                                      )}
                                  </div>
                                  <div>
                                      <h4 className="font-bold text-sm">{brand.name}</h4>
                                      <p className="text-[10px] text-gray-500 uppercase">{brand.download_count || 0} Downloads</p>
                                  </div>
                              </div>
                              
                              {brand.pdfUrl ? (
                                  <a href={brand.pdfUrl} target="_blank" rel="noopener noreferrer">
                                      <Button size="icon" variant="ghost" className="text-gray-400 hover:text-dreibach-teal">
                                          <Download size={18} />
                                      </Button>
                                  </a>
                              ) : (
                                  <span className="text-[10px] text-gray-500 italic px-2">No PDF</span>
                              )}
                          </div>
                      ))}
                  </div>
              )}
          </CardContent>
      </Card>
    </div>
  );
};

export default Downloads;
